import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { periodicReportsApi } from "../api/periodicReports.api";
import { userApi } from "../api/user.api";
import { utilsApi } from "../api/utils.api";
import {
  ITherapistActivityReportResponse,
  ITherapistActivityReportRequest,
} from "../types/periodicReports.types";
import { INumberOption } from "../../types/types";
import { TTherapist } from "../types/user.types";

export interface PeriodicReportsState {
  reports: ITherapistActivityReportResponse["rows"];
  reportSummary: ITherapistActivityReportResponse["summary"] | null;
  lastRequest: ITherapistActivityReportRequest | null;
  therapistOptions: INumberOption[];
  staffOptions: INumberOption[];
  serviceTypeOptions: INumberOption[];
}

const initialState: PeriodicReportsState = {
  reports: [],
  reportSummary: null,
  lastRequest: null,
  therapistOptions: [],
  staffOptions: [],
  serviceTypeOptions: [],
};

const periodicReportsSlice = createSlice({
  name: "periodicReports",
  initialState,
  reducers: {
    setReportData(
      state,
      action: PayloadAction<ITherapistActivityReportResponse>
    ) {
      state.reports = action.payload.rows;
      state.reportSummary = action.payload.summary;
    },
    setLastRequest(
      state,
      action: PayloadAction<ITherapistActivityReportRequest | null>
    ) {
      state.lastRequest = action.payload;
    },
    clearReportData(state) {
      state.reports = [];
      state.reportSummary = null;
      state.lastRequest = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addMatcher(
        periodicReportsApi.endpoints.getTherapistActivityReport.matchFulfilled,
        (state, { payload }) => {
          state.reports = payload.rows;
          state.reportSummary = payload.summary;
        }
      )
      .addMatcher(
        userApi.endpoints.getTherapistsByCompany.matchFulfilled,
        (state, { payload }) => {
          const list = Array.isArray(payload) ? payload : [];
          state.therapistOptions = list
            .map((t: TTherapist) => ({
              value: t.id,
              label: t.fullName,
            }));
        }
      )
      .addMatcher(
        userApi.endpoints.getStaffByCompany.matchFulfilled,
        (state, { payload }) => {
          const list = Array.isArray(payload) ? payload : [];
          state.staffOptions = list
            .map((t: TTherapist) => ({
              value: t.id,
              label: t.fullName,
            }));
        }
      )
      .addMatcher(
        utilsApi.endpoints.getCompanyServices.matchFulfilled,
        (state, { payload }) => {
          const list = payload?.CompanyServicesListResults ?? [];
          state.serviceTypeOptions = list.map((s) => ({
            value: s.id,
            label: s.name,
          }));
        }
      );
  },
});

export const { actions, reducer } = periodicReportsSlice;

export default periodicReportsSlice.reducer;
